/**
 * <SamSheet> — ce qui porte la conversation avec Sam par-dessus une activité (organism).
 *
 * Sam aide sur CET exercice : l'élève doit pouvoir relire l'énoncé sans fermer la conversation.
 * Ce n'est donc pas `<Modal>`, qui masque l'écran derrière un rideau sombre et le remplit :
 *  - sur un téléphone, une feuille basse à DEUX hauteurs (`detent`) : la basse laisse voir le
 *    haut de l'énoncé, la haute donne de la place à une longue réponse et au clavier ; la
 *    poignée passe de l'une à l'autre ;
 *  - quand l'écran est large, un panneau latéral à droite, sans poignée : l'activité reste
 *    entière à côté ;
 *  - dessous, un voile clair : l'énoncé recule au second plan et reste lisible. Le toucher ferme.
 *    Quand l'écran qui le porte lui a fait de la place à côté (`SamSheetBesideContext`, le live au
 *    grand écran en Concentré), le panneau n'a pas de voile : l'activité reste entière et touchable.
 *
 * La disposition se lit sur la largeur de la FENÊTRE (`layout="auto"`, le défaut), connue dès le
 * premier rendu : la feuille remplit l'écran qui la porte, et attendre la mesure de son conteneur
 * la dessinait d'abord étroite, en pleine largeur, une à deux images, avant qu'elle ne saute dans
 * la colonne de droite (recette du live au web, 20/10). L'ancien code réservait le panneau au web ;
 * l'écran peut forcer `"sheet"` sur une tablette s'il veut garder cette règle.
 *
 * Contrôlée : la hauteur (`detent`) arrive en prop, la poignée la demande (`onDetentChange`).
 * C'est l'écran qui passe à la haute quand le clavier s'ouvre, et qui fait glisser la feuille
 * sous le doigt : ici la poignée se touche.
 *
 * Elle entre (`theme.motion.standard`, la courbe de la marque) : le panneau glisse depuis le bord
 * droit, du même pas que l'activité qui lui fait de la place à côté ; la feuille basse monte de 12 px
 * en fondu. Le voile, lui, est là dès la première image : l'élève doit voir qu'il a ouvert un volet
 * qui cache quelque chose (Wajih, 20/10). En mouvement réduit, elle est aussitôt là. Elle n'a pas de
 * sortie : refermée, elle se démonte.
 *
 * Elle remplit le conteneur qui la porte (l'écran de l'activité) : c'est lui qui la monte et la
 * démonte. Son fond est celui d'une page, opaque : les bulles et le composeur, blancs, s'y
 * détachent, et l'énoncé ne se lit jamais au travers.
 */
import React, { createContext, useContext } from "react"
import { Animated, Pressable, useWindowDimensions, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { useFadeIn } from "../../hooks/useFadeIn"
import { shadowStyle } from "../../utils/shadow"
import { SAM_PANEL_FROM, SAM_PANEL_WIDTH } from "./samSheetGeometry"

export { SAM_PANEL_FROM, SAM_PANEL_WIDTH }

/**
 * L'écran qui porte la feuille a fait de la place au panneau, à côté de ce qu'il montre : le panneau
 * se pose sans voile. Sans effet sur la feuille basse. Défaut `false`.
 */
export const SamSheetBesideContext = createContext(false)

/** `auto` : feuille basse, ou panneau latéral à partir de `SAM_PANEL_FROM`. */
export type SamSheetLayout = "auto" | "sheet" | "panel"

/** Les deux hauteurs de la feuille : celle d'usage, et la haute. */
export type SamSheetDetent = "default" | "large"

export type SamSheetProps = {
    /** La conversation — `<SamConversation>`. */
    children: React.ReactNode
    /** Le voile touché. */
    onClose: () => void
    /** Défaut `auto`. */
    layout?: SamSheetLayout
    /** Défaut `default`. Sans effet sur le panneau latéral. */
    detent?: SamSheetDetent
    /** La poignée touchée : la hauteur à prendre. Absent, la poignée ne presse pas. */
    onDetentChange?: (detent: SamSheetDetent) => void
    /**
     * Le nom de la feuille pour un lecteur d'écran. Défaut « Conversation avec Sam » ; le chat
     * d'un live, qui se pose dans la même feuille, dit le sien.
     */
    accessibilityLabel?: string
    testID?: string
}

const TEXTS = {
    label: "Conversation avec Sam",
    close: "Fermer la conversation",
    grow: "Agrandir la conversation",
    shrink: "Réduire la conversation",
}

/** La part de l'écran que prend la feuille. */
const DETENT_HEIGHT: Record<SamSheetDetent, `${number}%`> = { default: "66%", large: "93%" }
/** L'activité derrière reste lisible : au-delà, le voile l'efface au lieu de la reculer. */
const VEIL_OPACITY = 0.32
const HANDLE = { width: 44, height: 5 }
/** La feuille basse monte d'autant en entrant, comme un bandeau (`<Snackbar>`). */
const RISE = 12

export function SamSheet(props: SamSheetProps) {
    const {
        children,
        onClose,
        layout = "auto",
        detent = "default",
        onDetentChange,
        accessibilityLabel = TEXTS.label,
        testID,
    } = props
    const { width } = useWindowDimensions()
    const panel = layout === "panel" || (layout === "auto" && width >= SAM_PANEL_FROM)
    const beside = useContext(SamSheetBesideContext) && panel
    const large = detent === "large"
    const next: SamSheetDetent = detent === "default" ? "large" : "default"
    // L'entrée de la feuille : de 0 (pas encore là) à 1, une fois au montage.
    const entry = useFadeIn(true)
    const arrival = panel
        ? { transform: [{ translateX: entry.interpolate({ inputRange: [0, 1], outputRange: [SAM_PANEL_WIDTH, 0] }) }] }
        : {
              opacity: entry,
              transform: [{ translateY: entry.interpolate({ inputRange: [0, 1], outputRange: [RISE, 0] }) }],
          }

    return (
        <View style={styles.layer} pointerEvents="box-none" testID={testID}>
            {!beside && <Pressable style={styles.veil} onPress={onClose} accessibilityLabel={TEXTS.close} />}

            <Animated.View
                style={[panel ? styles.panel : styles.sheet(DETENT_HEIGHT[detent]), arrival]}
                role="dialog"
                aria-label={accessibilityLabel}
            >
                {!panel && (
                    <Pressable
                        onPress={onDetentChange != null ? () => onDetentChange(next) : undefined}
                        disabled={onDetentChange == null}
                        accessibilityRole="button"
                        accessibilityLabel={large ? TEXTS.shrink : TEXTS.grow}
                        style={styles.handleZone}
                    >
                        <View style={styles.handle} />
                    </Pressable>
                )}
                {children}
            </Animated.View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    layer: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },
    veil: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: theme.colors.surface,
        opacity: VEIL_OPACITY,
    },
    sheet: (height: `${number}%`) => ({
        position: "absolute" as const,
        left: 0,
        right: 0,
        bottom: 0,
        height,
        backgroundColor: theme.colors.surface,
        borderTopLeftRadius: theme.radius.xl,
        borderTopRightRadius: theme.radius.xl,
        // Sur la nuit une ombre ne se voit pas : c'est ce filet qui détache la feuille de l'écran.
        borderTopWidth: 1,
        borderColor: theme.colors["border-subtle"],
        ...shadowStyle(theme, "sheet"),
    }),
    panel: {
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        width: SAM_PANEL_WIDTH,
        maxWidth: "100%",
        paddingTop: theme.spacing["2"],
        backgroundColor: theme.colors.surface,
        borderTopLeftRadius: theme.radius.lg,
        borderBottomLeftRadius: theme.radius.lg,
        borderLeftWidth: 1,
        borderColor: theme.colors["border-subtle"],
        ...shadowStyle(theme, "sheet"),
    },
    /** La barre ne fait que 5 px : c'est la zone autour qui la rend attrapable. */
    handleZone: {
        alignItems: "center",
        justifyContent: "center",
        paddingTop: theme.spacing["2"],
        paddingBottom: theme.spacing["1"],
    },
    handle: {
        width: HANDLE.width,
        height: HANDLE.height,
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.border,
    },
}))
